
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { UserIcon, Calendar } from "lucide-react";

interface RegistrationGuardProps {
  children: React.ReactNode;
  requiresProfile?: boolean;
}

const RegistrationGuard = ({ children, requiresProfile = true }: RegistrationGuardProps) => {
  const { isAuthenticated, profile, isLoading } = useAuth();
  const navigate = useNavigate();
  const [showRegistrationPrompt, setShowRegistrationPrompt] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        navigate('/login', { 
          state: { 
            from: location.pathname,
            message: "Você precisa estar logado para agendar uma consulta."
          }
        });
        return;
      }

      if (requiresProfile && !profile) {
        setShowRegistrationPrompt(true);
        return;
      }

      setShowRegistrationPrompt(false);
    }
  }, [isAuthenticated, profile, isLoading, navigate, requiresProfile]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (showRegistrationPrompt) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-primary" />
            </div>
            <CardTitle>Complete seu cadastro</CardTitle>
            <CardDescription>
              Você precisa completar seu perfil para agendar consultas na plataforma
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Calendar className="h-4 w-4" />
              <AlertDescription>
                Para garantir a segurança e qualidade do serviço, é necessário ter um perfil completo antes de agendar consultas.
              </AlertDescription>
            </Alert>
            
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => navigate('/register')}
                className="w-full"
              >
                Completar Cadastro
              </Button>
              
              <Button 
                variant="outline" 
                onClick={() => navigate('/')}
                className="w-full"
              >
                Voltar ao Início
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

export default RegistrationGuard;
